import React, { useMemo } from 'react';
import {
    Star,
    User,
    Calendar,
    ClipboardList,
    Check,
    BadgeCheck,
    X,
    ClipboardPen,
    Home,
} from 'lucide-react';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';

import { FlexColumn, Flex } from 'src/components/flex';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import type { ILead, IStageHistory, ILeadPropertyInterest } from 'src/services/api';
import { ELeadStage } from 'src/services/api/leads';
import { normalizeSource } from 'src/pages/landlord/analytics/utils';
import type { IOtherUser } from 'src/api/users-api';
import { normalizeIngestionMethod } from 'src/components/lead-sidebar/normalize-ingestion-method';

import { formatTimelineDate } from 'src/utils/format-date-time';
import { useAuthenticatedState } from 'src/authenticated-state/context';

import styles from 'src/components/lead-sidebar/LeadStageTimeline.module.css';

const MAX_INGESTION_SOURCE_LENGTH = 20;

const getIngestionSourceWithEllipsis = (source: string): string => {
    if (source.length <= MAX_INGESTION_SOURCE_LENGTH) {
        return source;
    }
    return `${source.substring(0, MAX_INGESTION_SOURCE_LENGTH)}...`;
};

export default function LeadStageTimeline({
    lead,
    stages,
    interests,
    teamMembers,
    isExpanded = true
}: {
    lead: ILead,
    stages: IStageHistory[],
    interests: ILeadPropertyInterest[],
    teamMembers: IOtherUser[],
    isExpanded?: boolean
}) {
    const { userSettings } = useAuthenticatedState();
    const leadOwner = teamMembers.find(member => member.id === lead.owner);

    const allStages = [
        {
            id: 1,
            label: 'New lead created',
            stageValue: ELeadStage.NEW,
            icon: <Star size={14} />,
            details: `From ${getIngestionSourceWithEllipsis(normalizeSource(lead.ingestion_source))} via ${normalizeIngestionMethod(lead.ingestion_method)}`
        },
        {
            id: 2,
            label: 'Lead contacted',
            stageValue: ELeadStage.CONTACTED,
            icon: <User size={14} />,
            details: `${leadOwner?.name ?? 'Unknown'} contacted the lead`,
        },
        {
            id: 3,
            label: 'Tour requested',
            stageValue: ELeadStage.TOUR_REQUESTED,
            icon: <Calendar size={14} />,
            details: `Tour requested by ${lead.name}`,
        },
        {
            id: 4,
            label: 'Tour scheduled',
            stageValue: ELeadStage.TOUR_SCHEDULED,
            icon: <Calendar size={14} />,
            details: `Tour scheduled for ${lead.name}`,
        },
        {
            id: 5,
            label: 'Application sent',
            stageValue: ELeadStage.INVITED_TO_APPLY,
            icon: <ClipboardList size={14} />,
            details: `Application sent to ${lead.name}`,
        },
        {
            id: 6,
            label: 'Application completed',
            stageValue: ELeadStage.APPLICATION_COMPLETED,
            icon: <Check size={14} />,
            details: `Application completed by ${lead.name}`,
        },
        {
            id: 7,
            label: 'Application approved',
            stageValue: ELeadStage.APPLICATION_APPROVED,
            icon: <BadgeCheck size={14} />,
            details: `Application approved for ${lead.name}`,
        },
        {
            id: 8,
            label: 'Not Interested',
            stageValue: ELeadStage.NOT_INTERESTED,
            icon: <X size={14} />,
            details: `${lead.name} no longer interested`,
        },
        {
            id: 9,
            label: 'Not Qualified',
            stageValue: ELeadStage.NOT_QUALIFIED,
            icon: <X size={14} />,
            details: `${lead.name} is not qualified`,
        },
        {
            id: 10,
            label: 'Lease Signed',
            stageValue: ELeadStage.LEASE_SIGNED,
            icon: <ClipboardPen size={14} />,
            details: `${lead.name} has signed the lease`,
        },
    ];

    // Create timeline events from stage history, sorted by creation date
    const timelineEvents = useMemo(() => {
        const stageEvents = [
            {
                kind: 'stage' as const,
                stage: allStages.find(s => s.stageValue === ELeadStage.NEW)!,
                timestamp: lead.inserted_at,
                isNewLeadStage: true,
                timeValue: formatTimelineDate(lead.inserted_at, userSettings.timezone_override)
            },
            ...stages
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                .map(history => ({
                    kind: 'stage' as const,
                    stage: allStages.find(s => s.stageValue === history.current_value)!,
                    timestamp: history.created_at,
                    isNewLeadStage: false,
                    history,
                    timeValue: formatTimelineDate(history.created_at, userSettings.timezone_override)
                }))
        ].filter(event => event.stage);

        const interestEvents = interests.map(interest => ({
            kind: 'interest' as const,
            interest,
            timestamp: interest.created_at,
            timeValue: formatTimelineDate(interest.created_at, userSettings.timezone_override)
        }));

        return [...stageEvents, ...interestEvents].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
    }, [allStages, lead, stages, interests, isExpanded, userSettings.timezone_override]);

    return (
        <ExpandableItem title="Timeline" defaultExpanded={isExpanded}>
            <div style={{ position: 'relative', paddingLeft: 48 }}>
                <FlexColumn gap={1}>
                    {timelineEvents.map((event, index) => {
                        const isLastEvent = index === timelineEvents.length - 1;

                        if (event.kind === 'interest') {
                            const { interest, timeValue } = event;
                            return (
                                <Flex key={`interest-${interest.id}`} alignItems="flex-start" style={{ position: 'relative' }}>
                                    {!isLastEvent && (
                                        <div className={styles.timelineLine} />
                                    )}
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: '#3B82F6',
                                            color: '#fff',
                                            position: 'absolute',
                                            left: -48,
                                        }}
                                    >
                                        <Home size={14} />
                                    </Avatar>
                                    <FlexColumn gap={0} sx={{ minWidth: 0 }}>
                                        <div className={styles.eventLabel}>Property interest</div>
                                        <Tooltip title={`${lead.name} showed interest in ${interest.property_name}`}>
                                            <Typography className={clsx(styles.eventDetails, 'ellipsisText')} variant="body2" color="textSecondary">
                                                {lead.name} showed interest in {interest.property_name}
                                            </Typography>
                                        </Tooltip>
                                        <Flex alignItems="center" className={styles.eventTimestamp}>
                                            <Calendar size={16} color="#6B7280" />
                                            {timeValue}
                                        </Flex>
                                    </FlexColumn>
                                </Flex>
                            );
                        }

                        const { stage, timestamp } = event;

                        return (
                            <Flex key={`${stage.stageValue}-${timestamp}`} alignItems="flex-start" style={{ position: 'relative' }}>
                                {/* Vertical line - only show if not the last event */}
                                {!isLastEvent && (
                                    <div className={styles.timelineLine} />
                                )}

                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: (stage.stageValue === ELeadStage.NOT_QUALIFIED || stage.stageValue === ELeadStage.NOT_INTERESTED) ? '#EF4444' : '#10B981',
                                        color: '#fff',
                                        position: 'absolute',
                                        left: -48,
                                    }}
                                >
                                    {stage.icon}
                                </Avatar>

                                <FlexColumn gap={0} sx={{ minWidth: 0 }}>
                                    <div className={styles.eventLabel}>
                                        {stage.label}
                                    </div>

                                    <Tooltip title={stage.details}>
                                        <Typography className={clsx(styles.eventDetails, 'ellipsisText')} variant="body2" color="textSecondary">
                                            {stage.details}
                                        </Typography>
                                    </Tooltip>

                                    <Flex alignItems="center" className={styles.eventTimestamp}>
                                        <Calendar size={16} color="#6B7280" />
                                        {event.timeValue}
                                    </Flex>
                                </FlexColumn>
                            </Flex>
                        );
                    })}
                </FlexColumn>
            </div>
        </ExpandableItem>
    );
}
